<template>
  <div class="flex flex-col sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-[520px]">
      <div class="items-center bg-white px-12 md:px-28 pb-6 shadow sm:rounded-lg">
        <div class="flex flex-col items-center justify-center pt-14">
          <Icon class="text-5xl text-primary" name="gpp_maybe" is-filled/>
          <SectionHeader
              class="mx-auto !justify-center pb-2"
              :title="__('Verify your code')"
          >
          </SectionHeader>
          <p class="text-gray-600 text-lg text-center mb-6">
            {{ __("We've sent a verification code to your email") }}
            {{ maskedEmail(props.email) }}
          </p>

        </div>
        <div>
          <form @submit.prevent="handleSubmit">
            <div class="flex flex-col">
              <TwoFactorTimer
                  ref="timeRef"
                  :expireTime="localExpireTime"
                  @expired="invalidCode"
              />

              <TwoFactorInput
                  v-model="values.code"
                  :errors="errors.code"
                  class="mt-4"
                  @update:errors="(e) => errors.code = e"
              ></TwoFactorInput>
              <div
                  v-if="errorMessage || successMessage"
                  class="mt-8 flex flex-col"
              >
                <div
                    v-if="successMessage"
                    class="flex items-center gap-2 rounded-lg border border-green-400 bg-green-100 p-4 text-green-700"
                >
                        <span
                            class="material-symbols-outlined text-xl text-green-600"
                            style="font-variation-settings: 'FILL' 1"
                        >
                          check_circle
                        </span>
                  <span>{{ successMessage }}</span>
                </div>
                <div
                    v-else-if="errorMessage"
                    class="flex items-center gap-2 rounded-lg border border-red-400 bg-red-100 p-4 text-red-700"
                >
                        <span
                            class="material-symbols-outlined text-xl text-red-600"
                            style="font-variation-settings: 'FILL' 1"
                        >
                          error
                        </span>
                  <span>{{ errorMessage }}</span>
                </div>
              </div>

            </div>

            <div
                class="mt-8 flex flex-col gap-2 md:flex-row-reverse md:justify-center items-center"
            >
              <Button
                  class="flex items-center justify-center gap-1.5"
                  :type="isLoading ? 'button' : 'submit'"
                  :disabled="isLoading"
                  style="width: 10rem !important"
              >
                <Icon
                    v-if="isLoading"
                    class="animate-spin text-lg"
                    name="progress_activity"
                />
                <span>{{ __('Verify') }}</span>
              </Button>
            </div>
          </form>
        </div>
        <div v-if="isExpired" class="text-center mt-6">
          <p class="text-sm font-semibold">
            {{ __("Didn't receive the code?") }}
            <a @click="resendCode" class="font-semibold underline text-primary cursor-pointer">{{ __('Resend') }}</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, watch} from 'vue';
import {__} from '@/helpers';
import {Button} from '../../../../components/block/button';
import {Icon} from '../../../../components/block/icon';
import {SectionHeader} from '../../../components/section-header';
import axios from 'axios';
import TwoFactorTimer from "../components/TwoFactorTimer.vue";
import TwoFactorInput from "../components/TwoFactorInput.vue";

const props = defineProps({
  successMessage: {
    type: String,
    default: null
  },
  errorMessage: {
    type: String,
    default: null
  },
  email: String,
  expireTime: String,
});

const values = ref({
  code: ''
});

const timeRef = ref(null);
const errorMessage = ref('');
const successMessage = ref('');
const isExpired = ref(false);
const localExpireTime = ref(props.expireTime);

const errors = ref({
  code: []
});

const isLoading = ref(false);

const maskedEmail = (email) => {
  if (!email) return '';
  const [name, domain] = email.split('@');
  return `${name[0]}****${name.slice(-1)}@${domain}`;
};

function invalidCode() {
  errorMessage.value = __('The verification code has expired. Please request a new one.');
  isExpired.value = true;
}

async function resendCode() {
  errorMessage.value = '';
  successMessage.value = '';
  try {
    const response = await axios.post(route('user.2fa.resend'), {email: props.email});

    if (response.data.status === 'success') {
      isExpired.value = false;
      errorMessage.value = '';
      successMessage.value = response.data.message;

      localExpireTime.value = response.data.expire;

      timeRef.value?.resetTimer();
    } else if (response.data.status === 'error') {
      errorMessage.value = error.response?.data?.message || 'Something went wrong.';
      successMessage.value = '';
    }
  } catch (error) {
    if (error?.response?.data?.status === 'error' && error?.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else {
      errorMessage.value = __('An unexpected error has occurred. Please try again later.');
    }
  }
}

async function handleSubmit() {
  const requiredFieldKeys = ['code'];
  let firstElement;
let submitted = true;
  requiredFieldKeys.forEach((key) => {
    if (!values.value[key]) {
      errors.value[key] = [__('This field is required')];

      const element = document.getElementById(key);

      if (element && !firstElement) {
        firstElement = element;
      }
    }
  });

  if (submitted && Object.values(errors.value).some((error) => error.length)) {
    if (firstElement) {
      firstElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const response = await axios.post(route('user.2fa.verify'), {
      code: values.value.code,
      email: props.email
    });

    if (response.data.status === 'success') {
      localStorage.removeItem('timerStartTime');
      window.location.href = response.data.redirect;
    }
    errorMessage.value = '';
  } catch (error) {
    if (error?.response?.data) {
      const { status, message } = error.response.data;
      if (status === "error" && message) {
        errorMessage.value = message;
        successMessage.value = '';
      } else {
        errorMessage.value = __('An unexpected error has occurred. Please try again later.');
      }
    } else {
      errorMessage.value = __('An unexpected error has occurred. Please try again later.');
    }
  }

  isLoading.value = false;
}

watch(isExpired, (newVal) => {
  if (!newVal) {
    errorMessage.value = '';
  }
});
</script>
